import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { auth, db, functions } from "../../firebase/firebase";
import { nameofTutorProfileSchemaCollection, nameofTutorProfileSchemaVerified, TutorProfileView, TutorSubject } from "../Types/TutorProfileSchema";
import { getPermission } from "./permissionFunctions";
import { Roles } from "../Types/PermissionSchema";
import { httpsCallable } from "firebase/functions";

export async function getAllTutorProfiles(): Promise<TutorProfileView[]> {
    const tutorSubjects: TutorProfileView[] = [];

    const tutorProfileQuery = query(collection(db, nameofTutorProfileSchemaCollection), where(nameofTutorProfileSchemaVerified, "==", true));
    const queryResult = await getDocs(tutorProfileQuery);
    queryResult.forEach((doc) => {
        tutorSubjects.push(
            {
                Id: doc.id,
                Name: doc.data().Name,
                Atar: doc.data().Atar,
                TutorTier: doc.data().TutorTier,
                Rating: doc.data().Rating,
                Schedule: doc.data().Schedule,
                Subjects: doc.data().Subjects,
                SchoolId: doc.data().SchoolId,
                Verified: doc.data().Verified,
            } as TutorProfileView);
    });
    return tutorSubjects;
}

export async function getTutorProfile(tutorId: string): Promise<TutorProfileView | undefined> {
    const tutorRef = doc(db, nameofTutorProfileSchemaCollection, tutorId);
    const tutor = await getDoc(tutorRef);
    const tutorData = tutor.data() as TutorProfileView;
    if (!tutorData) {
        return undefined;
    }
    return {
        Id: tutor.id,
        Name: tutorData.Name,
        Atar: tutorData.Atar,
        TutorTier: tutorData.TutorTier,
        Rating: tutorData.Rating,
        Schedule: tutorData.Schedule,
        Subjects: tutorData.Subjects,
        SchoolId: tutorData.SchoolId,
        Verified: tutorData.Verified,
    } as TutorProfileView;
}

export async function getCurrentTutorProfile(): Promise<TutorProfileView | undefined> {
    const permission = await getPermission();

    if (permission?.Role !== Roles.Tutor) {
        return undefined;
    }

    const tutorId = auth.currentUser?.uid;
    if (tutorId == null) {
        return undefined;
    }

    return await getTutorProfile(tutorId);
}

export async function addTutorSubject(tutorSubject: TutorSubject, tutorId: string): Promise<void> {
    if (auth.currentUser == null) {
        throw new Error("No user is currently signed in.");
    }

    const createTutorSubjectFunction = httpsCallable(functions, "addTutorSubject");

    await createTutorSubjectFunction({
        TutorID: tutorId, 
        SubjectID: tutorSubject.SubjectID, 
        Mark: tutorSubject.Mark,
    }).catch((error) => {
        throw new Error(error);
    });
}

export async function updateTutorSubject(tutorSubject: TutorSubject, tutorId: string): Promise<void> {
    if (auth.currentUser == null) {
        throw new Error("No user is currently signed in.");
    }

    const updateTutorSubjectFunction = httpsCallable(functions, "updateTutorSubject");

    await updateTutorSubjectFunction({
        TutorID: tutorId,
        SubjectID: tutorSubject.SubjectID,
        Mark: tutorSubject.Mark,
    }).catch((error) => {
        throw new Error(error);
    });
}

export async function removeTutorSubject(tutorSubject: TutorSubject, tutorId: string): Promise<void> {
    if (auth.currentUser == null) {
        throw new Error("No user is currently signed in.");
    }

    const removeTutorSubjectFunction = httpsCallable(functions, "removeTutorSubject");

    await removeTutorSubjectFunction({
        TutorID: tutorId,
        SubjectID: tutorSubject.SubjectID,
    }).catch((error) => {
        throw new Error(error);
    });
}